import { memo, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '@/hooks/useLanguageHook'
import { useAuth } from '@/contexts/AuthContextSupabase'
import menuConfig from '@/config/menuConfig.json'
import styles from './Sidebar.module.css'

interface NavItem {
  name: string
  nameJa: string
  path: string
  icon: string
}

interface NavSection {
  title: string
  titleJa: string
  items: NavItem[]
}

const navigationSections = menuConfig as NavSection[]
const allItems = navigationSections.flatMap((section) => section.items)

export const Sidebar = memo(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const { language } = useLanguage()
  const { isAdmin } = useAuth()

  // Filter sections based on permissions
  const filteredSections = useMemo(() => {
    return navigationSections.filter((section) => {
      // Hide "Master Data" section for non-admin users
      if (section.title === 'Master Data' && !isAdmin) {
        return false
      }
      return true
    })
  }, [isAdmin])

  const activePath = useMemo(() => {
    const currentPath = location.pathname

    // Special case: viewing existing order detail (/order-new/<id> where id != 0)
    // should highlight /orders menu instead of /order-new
    const orderDetailMatch = currentPath.match(/^\/order-new\/(.+)$/)
    if (orderDetailMatch) {
      const orderId = orderDetailMatch[1]
      // If it's a new order (id is 0 or 'new'), keep /order-new active
      // Otherwise, highlight /orders for existing order view
      if (orderId !== '0' && orderId !== 'new') {
        return '/orders'
      }
    }

    const matches = allItems.filter((item) => {
      if (item.path === '/') {
        return currentPath === '/'
      }
      return (
        currentPath === item.path || currentPath.startsWith(`${item.path}/`)
      )
    })

    if (matches.length > 0) {
      return matches.sort((a, b) => b.path.length - a.path.length)[0].path
    }
    return ''
  }, [location.pathname])

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarContent}>
        <nav className={styles.nav}>
          <div className={styles.navContent}>
            {filteredSections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <h2 className={styles.sectionTitle}>
                  {language === 'ja' ? section.titleJa : section.title}
                </h2>
                <ul className={styles.navList}>
                  {section.items.map((item) => {
                    const isActive = activePath === item.path
                    const displayName =
                      language === 'ja' ? item.nameJa : item.name

                    return (
                      <li key={item.path}>
                        <div
                          className={`${styles.navItem} ${
                            isActive
                              ? styles.navItemActive
                              : styles.navItemInactive
                          }`}
                          onClick={() => navigate(item.path)}
                          title={displayName}
                        >
                          <i className={`${item.icon} ${styles.navIcon}`} />
                          <span className={styles.navText}>{displayName}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>
        <div className={styles.buildInfo}>
          <small>Build: {import.meta.env.VITE_BUILD_HASH || 'dev'}</small>
        </div>
      </div>
    </aside>
  )
})

Sidebar.displayName = 'Sidebar'
